import { defineStore } from 'pinia';
import {
	query,
	limit,
	startAfter,
	onSnapshot,
	getCountFromServer,
	getDocs,
} from 'firebase/firestore';

export const useFirebaseTableStore = (
	{
		tableName,
		collection,
		filtersBuilderFunction,
		entityClass,
		documentMappingFunction,
		realtime,
	} = {
		tableName: undefined,
		collection: undefined,
		filtersBuilderFunction: undefined,
		entityClass: undefined,
		documentMappingFunction: undefined,
		realtime: false,
	},
) =>
	defineStore(`firebaseTableStore-${tableName}`, {
		state: () => {
			return {
				loading: false,
				data: [],
				documents: [],
				totalRecords: 0,
				lastRequestPagination: {},
				lastRequestFilters: {},
				lastRequestSorters: {},
				selection: {
					all: false,
					rows: [],
				},
				previousPageDocuments: [],
				unsubscribeListenerFunction: undefined,
			};
		},
		actions: {
			updateRow({ rowIdx, newRowData }) {
				this.data[rowIdx] = newRowData;
			},
			deleteRow({ rowIdx }) {
				this.data.splice(rowIdx, 1);
			},
			async fetchData({ pagination, filters, sorters, reverse }, append = false) {
				this.loading = true;
				// console.log('Fetching Table Data', { pagination, filters });
				this.lastRequestPagination = pagination;
				this.lastRequestFilters = filters;
				this.lastRequestSorters = sorters;

				try {
					if (pagination.page === 1) {
						this.previousPageDocuments = [];
						this.data = [];
						this.documents = [];
					}
					if (this.unsubscribeListenerFunction) {
						this.unsubscribeListenerFunction();
						this.unsubscribeListenerFunction = undefined;
					}

					const queryData = [collection];
					if (filtersBuilderFunction) {
						const res = filtersBuilderFunction(filters);
						if (res?.length) {
							queryData.push(...res);
						}
					}
					const perPage = pagination.limit;
					const docsToListenCount = this.append ? this.data.length + perPage : perPage;
					const snapshotQuery = query(...queryData, limit(docsToListenCount));

					// Get total count
					const totalRecordsQuery = query(...queryData);
					const total = (await getCountFromServer(totalRecordsQuery)).data().count;
					this.totalRecords = total;

					const getCounter = pagination.limit;

					// Add limit and startAfter
					queryData.push(limit(getCounter));

					if (pagination.pageAction === 'next') {
						if (this.documents.length) {
							queryData.push(startAfter(this.documents[this.documents.length - 1]));
						}
					} else if (pagination.pageAction === 'previous') {
						this.previousPageDocuments.splice(this.previousPageDocuments.length - 1, 1);
						if (this.previousPageDocuments.length > 1) {
							queryData.push(
								startAfter(
									this.previousPageDocuments[this.previousPageDocuments.length - 2],
								),
							);
						}
					} else {
						if (this.previousPageDocuments.length > 0) {
							console.error('previousPageDocuments.length should be 0');
						}
					}

					const res = await getDocs(query(...queryData));
					this.documents = res.docs;
					this.previousPageDocuments.push(this.documents[this.documents.length - 1]);
					const data = res.docs
						.map((doc) => {
							if (documentMappingFunction) {
								return documentMappingFunction(doc);
							} else if (!entityClass) {
								return doc.data();
							}
							return new entityClass(doc.data());
						})
						.filter((item) => item !== null);

					if (append) {
						if (reverse) {
							this.data.unshift(...data.reverse());
						} else {
							this.data.push(...data);
						}
					} else {
						this.data = data;
					}
					if (realtime) {
						this.listenForChanges(snapshotQuery, totalRecordsQuery, append);
					}
				} catch (err) {
					console.error(err);
					throw err;
				} finally {
					this.loading = false;
				}
			},
			async syncTotalRecords(totalRecordsQuery) {
				const total = (await getCountFromServer(totalRecordsQuery)).data().count;
				this.totalRecords = total;
			},
			listenForChanges(query, totalRecordsQuery, append) {
				try {
					const unsubscribe = onSnapshot(query, (querySnapshot) => {
						if (querySnapshot.metadata.fromCache) return;
						// console.log({ querySnapshot });
						if (append) {
							this.documents = querySnapshot.docs;
							const data = querySnapshot.docs
								.map((doc) => {
									if (documentMappingFunction) {
										return documentMappingFunction(doc);
									} else if (!entityClass) {
										return doc.data();
									}
									return new entityClass(doc.data());
								})
								.filter((item) => item !== null);
							this.data = data;
						} else {
							this.documents = querySnapshot.docs;
							if (this.previousPageDocuments.length) {
								this.previousPageDocuments.splice(this.previousPageDocuments.length - 1, 1);
							}
							this.previousPageDocuments.push(this.documents[this.documents.length - 1]);
							const data = querySnapshot.docs
								.map((doc) => {
									if (documentMappingFunction) {
										return documentMappingFunction(doc);
									} else if (!entityClass) {
										return doc.data();
									}
									return new entityClass(doc.data());
								})
								.filter((item) => item !== null);
							this.data = data;
						}
						this.syncTotalRecords(totalRecordsQuery);
					});
					this.unsubscribeListenerFunction = unsubscribe;
				} catch (error) {
					console.error('[Firestore Table] error in listenForChanges', error);
				}
			},
			refetchData() {
				this.fetchData({
					pagination: this.lastRequestPagination,
					filters: this.lastRequestFilters,
					sorters: this.lastRequestSorters,
				});
			},
		},
	})();
